import {
    PortfolioSummaryAggregatedData,
    PortfolioSummaryAggregatedDataGroup,
} from '../../store/data/PortfolioSummaryDataState';
import { AggregationField } from '../../aggregation/fields/aggregationFieldPortfolio';
import {
    TotalGroupName,
    PortfolioSummarySortableDataItem,
} from '../../aggregation/strategies/AggregationStrategy';
import { SidesGroupName } from '../../aggregation/strategies/AggregationStrategy';

import { getAggregationStrategy } from '../../aggregation/strategies/AggregationStrategyFactory';
import { QuantitiesOptions } from 'utils/components/QuantitiesOptions/QuanitiesOptions';

export interface PortfolioSummaryChartBarsData {
    buy: PortfolioSummaryChartGroupData[];
    sell: PortfolioSummaryChartGroupDataSell[];
    net: PortfolioSummaryChartGroupDataNet[];
    groupNames: string[];
}

const initialChartBarsData = {
    buy: [],
    sell: [],
    net: [],
    groupNames: [],
};

export interface PortfolioSummaryChartGroupData {
    groupName: string;
    tradedValueLoc: number;
    remainingValueLoc: number;
    totalValueLoc: number;
}

export interface PortfolioSummaryChartGroupDataNet
    extends PortfolioSummaryChartGroupData {
    orderedValueLoc: number;
}

export interface PortfolioSummaryChartGroupDataSell
    extends PortfolioSummaryChartGroupData {
    shortTradedValueLoc: number;
    shortRemainingValueLoc: number;
    shortTotalValueLoc: number;
}

export function toPortfolioSummaryChartBarData(
    aggregationField: AggregationField,
    exchangeRate: number,
    aggregatedData: PortfolioSummaryAggregatedData | undefined,
    showBuysAsPositive: boolean,
    quantities: QuantitiesOptions,
    combineSellsAndShorts: boolean
): PortfolioSummaryChartBarsData {
    if (!aggregatedData || Object.keys(aggregatedData).length === 0) {
        return initialChartBarsData;
    }

    if (aggregationField === 'side') {
        return collateSideData(
            exchangeRate,
            aggregatedData,
            showBuysAsPositive,
            quantities
        );
    }

    const buys: PortfolioSummaryChartGroupData[] = [];
    const sells: PortfolioSummaryChartGroupDataSell[] = [];
    const net: PortfolioSummaryChartGroupDataNet[] = [];
    const totalsByGroupName: PortfolioSummarySortableDataItem[] = [];

    const getSortValue = (gr: PortfolioSummaryAggregatedDataGroup) => {
        switch (quantities) {
            case 'leaves':
                return gr.total.leavesValue;
            case 'traded':
                return gr.total.tradedValue;
            default:
                return gr.total.orderedValue;
        }
    };
    for (const groupName of Object.keys(aggregatedData)) {
        const group = aggregatedData[groupName];
        if (group.total) {
            buys.push(toBuyBar(exchangeRate, groupName, group, quantities));
            sells.push(toSellBar(exchangeRate, groupName, group, quantities));
            net.push(
                toNetBar(
                    exchangeRate,
                    groupName,
                    group,
                    quantities,
                    showBuysAsPositive
                )
            );

            totalsByGroupName.push({
                groupName,
                orderedValueLoc: getSortValue(group),
            });
        }
    }

    const aggregationStrategy = getAggregationStrategy(
        aggregationField,
        combineSellsAndShorts
    );
    const groupNames = totalsByGroupName
        // This sort is by descending value...
        .sort((x, y) => aggregationStrategy.sort(x, y))
        .map((g) => g.groupName)
        // Chart draws array from bottom to the top, so we need the smallest first...
        .reverse();

    return { buy: buys, sell: sells, net, groupNames };
}

function collateSideData(
    exchangeRate: number,
    aggregatedData: PortfolioSummaryAggregatedData,
    showBuysAsPositive: boolean,
    quantities: QuantitiesOptions
): PortfolioSummaryChartBarsData {
    // For side we have one "total" bar...
    const buyBar = toBuyBar(
        exchangeRate,
        TotalGroupName,
        aggregatedData[SidesGroupName],
        quantities
    );
    const sellBar = toSellBar(
        exchangeRate,
        TotalGroupName,
        aggregatedData[SidesGroupName],
        quantities
    );
    const netBar = toNetBar(
        exchangeRate,
        TotalGroupName,
        aggregatedData[SidesGroupName],
        quantities,
        showBuysAsPositive
    );
    return {
        buy: [buyBar],
        sell: [sellBar],
        net: [netBar],
        groupNames: [TotalGroupName],
    };
}

function toBuyBar(
    exchangeRate: number,
    groupName: string,
    group: PortfolioSummaryAggregatedDataGroup,
    quantities: QuantitiesOptions
): PortfolioSummaryChartGroupData {
    const tradedValueLoc = group.buy.tradedValue * exchangeRate;
    const remainingValueLoc = group.buy.leavesValue * exchangeRate;
    return applyQuantities(
        {
            groupName,
            tradedValueLoc,
            remainingValueLoc,
            totalValueLoc: tradedValueLoc + remainingValueLoc,
        },
        quantities
    );
}

function toSellBar(
    exchangeRate: number,
    groupName: string,
    group: PortfolioSummaryAggregatedDataGroup,
    quantities: QuantitiesOptions
): PortfolioSummaryChartGroupDataSell {
    const tradedValueLoc = group.sell.tradedValue * exchangeRate;
    const remainingValueLoc = group.sell.leavesValue * exchangeRate;
    const ret: PortfolioSummaryChartGroupDataSell = {
        groupName,
        tradedValueLoc,
        remainingValueLoc,
        totalValueLoc: tradedValueLoc + remainingValueLoc,
        shortTradedValueLoc: 0,
        shortRemainingValueLoc: 0,
        shortTotalValueLoc: 0,
    };
    if (group.short) {
        ret.shortTradedValueLoc = group.short.tradedValue * exchangeRate;
        ret.shortRemainingValueLoc = group.short.leavesValue * exchangeRate;
        ret.shortTotalValueLoc =
            ret.shortTradedValueLoc + ret.shortRemainingValueLoc;
    }
    return applyQuantities(ret, quantities);
}

function toNetBar(
    exchangeRate: number,
    groupName: string,
    group: PortfolioSummaryAggregatedDataGroup,
    quantities: QuantitiesOptions,
    showBuysAsPositive: boolean
): PortfolioSummaryChartGroupDataNet {
    const mult = showBuysAsPositive ? 1 : -1;
    const tradedValueLoc = group.net.tradedValue * exchangeRate * mult;
    const remainingValueLoc = group.net.leavesValue * exchangeRate * mult;
    const orderedValueLoc = tradedValueLoc + remainingValueLoc;
    return {
        groupName,
        tradedValueLoc: quantities === 'traded' ? tradedValueLoc : 0,
        remainingValueLoc: quantities === 'leaves' ? remainingValueLoc : 0,
        orderedValueLoc: quantities === 'ordered' ? orderedValueLoc : 0,
        totalValueLoc:
            quantities === 'traded'
                ? tradedValueLoc
                : quantities === 'leaves'
                ? remainingValueLoc
                : orderedValueLoc,
    };
}

const isSellData = (
    data: PortfolioSummaryChartGroupData &
        Partial<PortfolioSummaryChartGroupDataSell>
): data is PortfolioSummaryChartGroupDataSell =>
    data.shortTotalValueLoc !== undefined;

function applyQuantities<
    T extends
        | PortfolioSummaryChartGroupData
        | PortfolioSummaryChartGroupDataSell,
>(data: T, quantities: QuantitiesOptions): T {
    if (quantities === 'leaves') {
        data.tradedValueLoc = 0;
        data.totalValueLoc = data.remainingValueLoc;

        if (isSellData(data)) {
            data.shortTradedValueLoc = 0;
            data.shortTotalValueLoc = data.shortRemainingValueLoc;
        }
    }
    if (quantities === 'traded') {
        data.remainingValueLoc = 0;
        data.totalValueLoc = data.tradedValueLoc;
        if (isSellData(data)) {
            data.shortRemainingValueLoc = 0;
            data.shortTotalValueLoc = data.shortTradedValueLoc;
        }
    }
    return data;
}
